import type { BellConnection } from '../types';
import { startBellStream, type BellStream, type BellStreamHandlers } from './bell-stream';

/** The fallback leader announces itself this often... */
const HEARTBEAT_MS = 5_000;
/** ...and a follower takes over after this many beats go unheard (AC7.4). */
const MISSED_BEATS = 2;
/** How often a fallback follower checks for a silent leader. */
const WATCH_MS = 1_000;
/** How long a new fallback tab listens for an existing leader before claiming. */
const HELLO_WAIT_MS = 1_000;

export interface BellLeaderOptions {
  accountId: string;
  tenantId: string;
  /** Every tab: something changed, refetch (the caller debounces). */
  onChanged(): void;
  /** Every tab: the browser's one stream went live or is reconnecting. */
  onStatus(status: BellConnection): void;
}

/** The browser APIs the election uses; tests pass fakes, `null` means absent. */
export interface BellLeaderDeps {
  locks?: LockManager | null;
  createChannel?: (name: string) => BroadcastChannel | null;
  startStream?: (handlers: BellStreamHandlers) => BellStream;
}

export interface BellMembership {
  /** Sign-out or unmount: abort the stream if leading and release leadership. */
  leave(): void;
}

type RelayMessage =
  | { t: 'changed' }
  | { t: 'status'; s: BellConnection }
  | { t: 'hello' }
  | { t: 'beat'; id: string }
  | { t: 'bye' };

/**
 * Join this browser's bell for one (account, tenant): ONE stream per browser,
 * whatever the number of tabs (AC7.4). The stream costs one of the browser's
 * six HTTP/1.1 connections, so each tab holding its own would starve the app.
 *
 * - With Web Locks, the tab holding `awards-bell:<accountId>:<tenantId>` leads
 *   for as long as it lives; the browser hands the lock to the next tab when
 *   the leader closes.
 * - Without Web Locks, a BroadcastChannel heartbeat elects the leader: it beats
 *   every 5 s, a follower claims after 2 missed beats, and when two claim at
 *   once the lower id keeps it.
 * - Without BroadcastChannel nothing can be relayed, so every tab streams.
 *
 * The leader relays `changed` and its connection state to the other tabs over
 * the channel of the same name.
 */
export function joinBell(opts: BellLeaderOptions, deps: BellLeaderDeps = {}): BellMembership {
  const name = `awards-bell:${opts.accountId}:${opts.tenantId}`;
  const locks = deps.locks === undefined ? browserLocks() : deps.locks;
  const channel = (deps.createChannel ?? browserChannel)(name);
  const startStream = deps.startStream ?? ((handlers: BellStreamHandlers) => startBellStream(handlers));

  let stream: BellStream | null = null;
  let lastStatus: BellConnection | null = null;
  const post = (message: RelayMessage) => channel?.postMessage(message);

  const lead = () => {
    if (stream) return;
    stream = startStream({
      onChanged: () => {
        opts.onChanged();
        post({ t: 'changed' });
      },
      onStatus: (s) => {
        lastStatus = s;
        opts.onStatus(s);
        post({ t: 'status', s });
      },
    });
  };
  const resign = () => {
    stream?.stop();
    stream = null;
    lastStatus = null;
  };
  const relayed = (message: RelayMessage) => {
    if (message.t === 'changed') opts.onChanged();
    else if (message.t === 'status') opts.onStatus(message.s);
  };

  if (!channel) {
    lead();
    return { leave: resign };
  }
  return locks ? lockElection(locks, channel) : heartbeatElection(channel);

  function lockElection(manager: LockManager, ch: BroadcastChannel): BellMembership {
    const waiting = new AbortController();
    let left = false;
    let release: (() => void) | null = null;
    manager
      .request(name, { signal: waiting.signal }, () =>
        new Promise<void>((resolve) => {
          if (left) return resolve();
          release = resolve;
          lead();
        }),
      )
      .catch(() => undefined); // aborted while waiting: this tab left first
    ch.onmessage = (e: MessageEvent<RelayMessage>) => {
      // A tab that joins late learns the current state from the leader.
      if (e.data.t === 'hello') {
        if (stream && lastStatus) post({ t: 'status', s: lastStatus });
      } else relayed(e.data);
    };
    post({ t: 'hello' });
    return {
      leave() {
        left = true;
        waiting.abort();
        resign();
        release?.();
        ch.close();
      },
    };
  }

  function heartbeatElection(ch: BroadcastChannel): BellMembership {
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    let lastBeat = 0;
    let beating: ReturnType<typeof setInterval> | undefined;
    const claim = () => {
      if (stream) return;
      lead();
      post({ t: 'beat', id });
      beating = setInterval(() => post({ t: 'beat', id }), HEARTBEAT_MS);
    };
    const stepDown = () => {
      clearInterval(beating);
      beating = undefined;
      resign();
    };
    ch.onmessage = (e: MessageEvent<RelayMessage>) => {
      const message = e.data;
      if (message.t === 'beat') {
        lastBeat = Date.now();
        if (stream && message.id < id) stepDown();
        else if (stream) post({ t: 'beat', id });
      } else if (message.t === 'hello') {
        if (!stream) return;
        post({ t: 'beat', id });
        if (lastStatus) post({ t: 'status', s: lastStatus });
      } else if (message.t === 'bye') {
        claim();
      } else relayed(message);
    };
    const hello = setTimeout(() => {
      if (lastBeat === 0) claim();
    }, HELLO_WAIT_MS);
    const watch = setInterval(() => {
      if (!stream && lastBeat !== 0 && Date.now() - lastBeat > HEARTBEAT_MS * MISSED_BEATS) claim();
    }, WATCH_MS);
    post({ t: 'hello' });
    return {
      leave() {
        clearTimeout(hello);
        clearInterval(watch);
        if (stream) post({ t: 'bye' });
        stepDown();
        ch.close();
      },
    };
  }
}

function browserLocks(): LockManager | null {
  return typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks ? navigator.locks : null;
}

function browserChannel(name: string): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name);
}
